import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { MARD_291, PALETTE_BRAND, filterPaletteByCode, readableTextColor } from '../core/palette';

interface Props {
  selectedColor: number | null;
  onSelect: (index: number) => void;
  onClose: () => void;
}

export function QuickPaletteModal({ selectedColor, onSelect, onClose }: Props) {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const colors = useMemo(() => {
    return filterPaletteByCode(MARD_291.map((color, index) => ({ ...color, index })), query, (color) => color.code);
  }, [query]);

  useEffect(() => {
    searchRef.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="quick-palette-layer" onMouseDown={onClose}>
      <section className="quick-palette-dialog" role="dialog" aria-modal="true" aria-labelledby="quick-palette-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="quick-palette-head">
          <div><p className="eyebrow">QUICK PALETTE · P</p><h2 id="quick-palette-title">快速选择色卡</h2><span>输入色号后按空格，直接选择第一项；按 Esc 关闭。</span></div>
          <button type="button" onClick={onClose} aria-label="关闭色卡"><X size={19} /></button>
        </header>
        <label className="quick-palette-search"><Search size={17} /><input ref={searchRef} value={query} onBeforeInput={(event) => {
          const value = (event.nativeEvent as InputEvent).data;
          if (value && /[^a-z0-9]/i.test(value)) event.preventDefault();
        }} onChange={(event) => setQuery(event.target.value.replace(/[^a-z0-9]/gi, ''))} onKeyDown={(event) => {
          if ((event.key !== ' ' && event.code !== 'Space') || event.nativeEvent.isComposing) return;
          event.preventDefault();
          event.stopPropagation();
          const firstResult = colors[0];
          if (firstResult) onSelect(firstResult.index);
        }} aria-label="搜索色号，只能输入字母和数字，按空格选择第一项" placeholder="输入色号，例如 G12" inputMode="text" autoCapitalize="characters" autoComplete="off" spellCheck={false} maxLength={16} /><kbd>空格</kbd></label>
        <div className="quick-palette-grid">
          {colors.map((color, position) => (
            <button key={color.index} className={`${selectedColor === color.index ? 'active' : ''}${position === 0 ? ' default-result' : ''}`} type="button" onClick={() => onSelect(color.index)} aria-label={`${PALETTE_BRAND} ${color.code}${position === 0 ? '，空格默认选择' : ''}`} title={`${PALETTE_BRAND} ${color.code}${position === 0 ? ' · 空格选择' : ''}`}>
              <i style={{ background: color.hex, color: readableTextColor(color.hex) }}>{color.code}</i>
              <span><small>{PALETTE_BRAND}</small><strong>{color.code}</strong></span>
              {selectedColor === color.index && <Check size={17} />}
            </button>
          ))}
        </div>
        {!colors.length && <div className="quick-palette-empty">没有找到这个色号</div>}
      </section>
    </div>
  );
}
